import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clearDecisionContext, loadDecisionContext, type DecisionContext } from '../inbox/storage';
import {
  calculateGrowth,
  calculatePlanning,
  calculatePricing,
  calculateProfitability,
} from '../workspace/calculations';
import { loadBusinessWorkspace, resetBusinessWorkspace, saveBusinessWorkspace } from '../workspace/storage';
import type { BusinessWorkspace } from '../workspace/types';

export type DecisionToolKind = 'profitability' | 'pricing' | 'planning' | 'growth';

type NumericKey = Exclude<keyof BusinessWorkspace, 'updatedAt'>;

const toolMeta: Record<DecisionToolKind, { title: string; eyebrow: string; description: string }> = {
  profitability: {
    title: '수익성 분석',
    eyebrow: 'PROFITABILITY STUDIO',
    description: '매출과 비용 구조를 한 번에 넣고 실제 이익, 마진, 비용률을 확인합니다.',
  },
  pricing: {
    title: '가격·견적',
    eyebrow: 'PRICING & QUOTE STUDIO',
    description: '원가, 수수료, 목표마진을 기준으로 현재 가격과 목표 가격을 비교합니다.',
  },
  planning: {
    title: '손익·현금흐름',
    eyebrow: 'BREAK-EVEN & CASH PLANNER',
    description: '월 판매량, 고정비, 보유현금을 바꿔 손익분기와 목표이익 달성 수준을 확인합니다.',
  },
  growth: {
    title: '광고·성장',
    eyebrow: 'GROWTH & ROAS STUDIO',
    description: '광고비와 광고매출, 마진을 연결해 ROAS와 손익분기 광고수준을 판단합니다.',
  },
};

const fieldLabels: Partial<Record<NumericKey, string>> = {
  revenue: '매출·계약금액',
  directCost: '직접원가·재료/외주비',
  variableCost: '기타 변동비',
  fixedCost: '월 고정비',
  unitPrice: '판매가·평균 객단가',
  unitCost: '단위원가',
  unitOtherCost: '단위 기타비용',
  feeRate: '수수료율 (%)',
  units: '월 예상 판매량',
  targetMarginRate: '목표 마진율 (%)',
  targetProfit: '목표 이익',
  cashBalance: '현재 가용현금',
  adSpend: '광고비',
  attributedRevenue: '광고 기여매출',
  grossMarginRate: '광고 전 마진율 (%)',
};

function money(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '계산 불가';
  return `${Math.round(value).toLocaleString('ko-KR')}원`;
}

function percent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '계산 불가';
  return `${value.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function dateLabel(value?: string): string {
  if (!value) return '미제공';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '미제공';
  return new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(parsed);
}

function Metric({ label, value, emphasis = false }: Readonly<{ label: string; value: string; emphasis?: boolean }>) {
  return (
    <div className={`studio-metric${emphasis ? ' studio-metric-emphasis' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function WorkspaceInput({
  workspace,
  field,
  onChange,
}: Readonly<{ workspace: BusinessWorkspace; field: NumericKey; onChange(field: NumericKey, value: number): void }>) {
  return (
    <label className="studio-field">
      <span>{fieldLabels[field] ?? field}</span>
      <input
        type="number"
        min="0"
        step={field.toLowerCase().includes('rate') ? '0.1' : '1'}
        value={workspace[field] || ''}
        onChange={(event) => {
          const next = Number(event.target.value);
          onChange(field, Number.isFinite(next) && next >= 0 ? next : 0);
        }}
      />
    </label>
  );
}

function ToolNavigation({ active }: Readonly<{ active: DecisionToolKind }>) {
  const items: Array<[DecisionToolKind, string]> = [
    ['profitability', '수익성'],
    ['pricing', '가격·견적'],
    ['planning', '손익·현금'],
    ['growth', '광고·성장'],
  ];

  return (
    <nav className="studio-tabs" aria-label="사업 의사결정 도구">
      {items.map(([kind, label]) => (
        <Link key={kind} className={kind === active ? 'studio-tab-active' : ''} to={`/tools/${kind}`}>
          {label}
        </Link>
      ))}
    </nav>
  );
}

function DecisionContextEvidence({ context }: Readonly<{ context: DecisionContext }>) {
  const facts: Array<{ label: string; value: string }> = [];
  if (context.organization) facts.push({ label: '기관/사업주체', value: context.organization });
  if (context.region) facts.push({ label: '지역', value: context.region });
  if (context.closesAt) facts.push({ label: '마감', value: dateLabel(context.closesAt) });

  if (context.kind === 'HOUSING') {
    if (context.priceMax !== undefined) facts.push({ label: '최고분양가', value: money(context.priceMax) });
    if (context.competitionRate !== undefined) facts.push({ label: '경쟁률', value: `${context.competitionRate.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}:1` });
    if (context.nearbyMedianSalePrice !== undefined) facts.push({ label: '주변 실거래 중앙값', value: `${money(context.nearbyMedianSalePrice)}${context.marketSampleCount !== undefined ? ` · ${context.marketSampleCount.toLocaleString('ko-KR')}건` : ''}` });
    if (context.marketReferenceHouseType) facts.push({ label: '시세 비교 주택형', value: `${context.marketReferenceHouseType}${context.marketReferenceAreaSqm !== undefined ? ` · ${context.marketReferenceAreaSqm.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}㎡` : ''}${context.marketReferenceAreaBasis === 'HOUSE_TYPE_PROXY' ? ' · 주택형 프록시' : context.marketReferenceAreaBasis === 'OFFICIAL_EXCLUSIVE' ? ' · 공식 전용면적' : ''}` });
    if (context.marketReferenceEstimatedSalePrice !== undefined) facts.push({ label: '비교면적 보정 시세', value: money(context.marketReferenceEstimatedSalePrice) });
    if (context.nearbyMedianUnitPricePerSqm !== undefined) facts.push({ label: '실거래 전용면적 평당가', value: `${Math.round(context.nearbyMedianUnitPricePerSqm * 3.3058 / 10_000).toLocaleString('ko-KR')}만원/평` });
    if (context.marketComparisonDetail) facts.push({ label: '시세 표본 범위', value: context.marketComparisonDetail });
    if (context.housingUnitTypes?.length) facts.push({ label: '주택형 근거', value: `${context.housingUnitTypes.length.toLocaleString('ko-KR')}개 주택형` });
  }

  if (context.kind === 'SUPPORT') {
    if (context.amountDetail || context.amountProvenance) facts.push({
      label: '지원한도 근거',
      value: context.amountDetail ?? (context.amountProvenance === 'DERIVED' ? '공식 사업개요 문구에서 구조화' : '공식 구조화 값'),
    });
    if (context.executingOrganization) facts.push({ label: '수행기관', value: context.executingOrganization });
    if (context.applicationMethod) facts.push({ label: '신청방법', value: context.applicationMethod });
    if (context.selfContributionRate !== undefined) facts.push({ label: '자부담', value: `${context.selfContributionRate.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%` });
    if (context.contact) facts.push({ label: '문의처', value: context.contact });
  }

  if (context.kind === 'BID') {
    if (context.basePrice !== undefined) facts.push({ label: '기초금액', value: money(context.basePrice) });
    if (context.qualificationSummary) facts.push({ label: '참가자격', value: context.qualificationSummary });
    if (context.industryCodes?.length) facts.push({ label: '업종·면허', value: context.industryCodes.join(' · ') });
    if (context.historicalAwardRate !== undefined) facts.push({ label: '최근 낙찰률 중앙값', value: `${context.historicalAwardRate.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}%` });
    if (context.historicalContractCount !== undefined) facts.push({ label: '최근 계약 표본', value: `${context.historicalContractCount.toLocaleString('ko-KR')}건` });
  }

  if (context.kind === 'COMMERCIAL') {
    if (context.radiusMeters !== undefined) facts.push({ label: '분석 반경', value: `${context.radiusMeters.toLocaleString('ko-KR')}m` });
    if (context.totalStoreCount !== undefined) facts.push({ label: '전체 점포', value: `${context.totalStoreCount.toLocaleString('ko-KR')}개` });
    if (context.sampledStoreCount !== undefined) facts.push({ label: '분석 표본', value: `${context.sampledStoreCount.toLocaleString('ko-KR')}개` });
    if (context.sameIndustrySampleCount !== undefined) facts.push({ label: '동종업종 표본', value: `${context.sameIndustrySampleCount.toLocaleString('ko-KR')}개` });
    if (context.competitionScore !== undefined) facts.push({ label: '경쟁강도', value: `${context.competitionScore}/100` });
    if (context.sampleCoverage !== undefined) facts.push({ label: '표본 커버리지', value: `${context.sampleCoverage.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%` });
    if (context.densityLevel) facts.push({ label: '밀도구간', value: context.densityLevel === 'HIGH' ? '높음' : context.densityLevel === 'MEDIUM' ? '보통' : '낮음' });
    if (context.categoryBreakdown?.length) facts.push({ label: '주요 업종 구성', value: context.categoryBreakdown.slice(0, 4).map((entry) => `${entry.category} ${entry.count}`).join(' · ') });
    if (context.sameIndustryStores?.length) facts.push({ label: '가까운 동종업체', value: context.sameIndustryStores.slice(0, 4).map((store) => store.name).join(' · ') });
  }

  const hasLinks = Boolean(context.applicationUrl || context.attachmentUrl || context.sourceUrl);
  if (facts.length === 0 && !hasLinks) return null;

  return (
    <details className="domain-evidence decision-context-evidence" open>
      <summary>함께 전달된 판단 근거</summary>
      {facts.length > 0 ? (
        <dl className="domain-evidence-list">
          {facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
        </dl>
      ) : null}
      {hasLinks ? (
        <div className="domain-evidence-links">
          {context.applicationUrl ? <a href={context.applicationUrl} target="_blank" rel="noreferrer">사업 신청 페이지</a> : null}
          {context.attachmentUrl ? <a href={context.attachmentUrl} target="_blank" rel="noreferrer">{context.attachmentName ?? '공고 첨부파일'}</a> : null}
          {context.sourceUrl ? <a href={context.sourceUrl} target="_blank" rel="noreferrer">공식 원문</a> : null}
        </div>
      ) : null}
      {context.kind === 'BID' && (context.historicalAwardRate !== undefined || context.historicalContractCount !== undefined) ? <p className="domain-evidence-note">과거 낙찰·계약 정보는 현재 공고의 낙찰 가능성을 보장하지 않는 비교자료입니다.</p> : null}
      {context.kind === 'HOUSING' ? <p className="domain-evidence-note">분양 평당가는 공급면적 기준이고 국토부 실거래 단가는 전용면적 기준입니다. 서로 다른 면적 기준의 평당가를 직접 비교하지 않습니다.</p> : null}
      {context.kind === 'COMMERCIAL' ? <p className="domain-evidence-note">상권 경쟁강도와 업종 수치는 현재 API 응답 표본을 바탕으로 한 탐색 근거이며 매출을 자동 추정하지 않습니다.</p> : null}
    </details>
  );
}

export function DecisionStudioPage({ kind }: Readonly<{ kind: DecisionToolKind }>) {
  const [workspace, setWorkspace] = useState<BusinessWorkspace>(() => loadBusinessWorkspace());
  const [decisionContext, setDecisionContext] = useState(() => kind === 'profitability' ? loadDecisionContext() : null);
  const [siteRevenuePerSqm, setSiteRevenuePerSqm] = useState(0);
  const meta = toolMeta[kind];
  const profitability = useMemo(() => calculateProfitability(workspace), [workspace]);
  const pricing = useMemo(() => calculatePricing(workspace), [workspace]);
  const planning = useMemo(() => calculatePlanning(workspace), [workspace]);
  const growth = useMemo(() => calculateGrowth(workspace), [workspace]);
  const isStarterWorkspace = workspace.updatedAt === null;
  const siteRevenueEstimate = decisionContext?.areaSqm && siteRevenuePerSqm > 0
    ? decisionContext.areaSqm * siteRevenuePerSqm
    : null;
  const contextAmountIsSupport = decisionContext?.amountRole === 'SUPPORT_AMOUNT' || decisionContext?.kind === 'SUPPORT';
  const contextOriginLabel = decisionContext?.originMode === 'DEMO'
    ? '예시 데이터에서 가져옴'
    : decisionContext?.originMode === 'LIVE'
      ? '공식 데이터에서 가져옴'
      : 'Radar에서 가져온 정보';

  function update(field: NumericKey, value: number): void {
    const next = { ...workspace, [field]: value, updatedAt: new Date().toISOString() };
    setWorkspace(next);
    saveBusinessWorkspace(next);
  }

  function reset(): void {
    setWorkspace(resetBusinessWorkspace());
  }

  function dismissDecisionContext(): void {
    clearDecisionContext();
    setDecisionContext(null);
  }

  return (
    <>
      <section className="studio-hero">
        <div>
          <p className="eyebrow">{meta.eyebrow}</p>
          <h1>{meta.title}</h1>
          <p>{meta.description}</p>
        </div>
        <div className="workspace-status">
          <strong>공통 Workspace</strong>
          <span>입력한 숫자는 다른 업무도구에도 이어집니다.</span>
          <button type="button" onClick={reset}>입력값 지우기</button>
        </div>
      </section>

      <ToolNavigation active={kind} />

      {kind === 'profitability' && decisionContext ? (
        <section className="decision-context-banner" aria-labelledby="decision-context-title">
          <div>
            <p className="eyebrow">{contextOriginLabel}</p>
            <h2 id="decision-context-title">{decisionContext.title}</h2>
            <p>{decisionContext.originMode === 'DEMO'
              ? `${decisionContext.sourceLabel} 화면 확인용 예시 데이터에서 가져온 검토 항목입니다. 실제 사업 판단에는 사용하지 마세요.`
              : `${decisionContext.sourceLabel}에서 가져온 검토 항목입니다. 공개된 숫자는 자동 가정하지 않고 사용자가 선택할 때만 Workspace에 적용합니다.`}</p>
            <div className="decision-context-facts">
              {decisionContext.amount !== undefined ? <span>{contextAmountIsSupport ? '지원한도' : '표시 금액'} {money(decisionContext.amount)}</span> : null}
              {decisionContext.areaSqm !== undefined ? <span>연면적 {decisionContext.areaSqm.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}㎡</span> : null}
              {decisionContext.units !== undefined ? <span>규모 {decisionContext.units.toLocaleString('ko-KR')}</span> : null}
              {decisionContext.target ? <span>대상 {decisionContext.target}</span> : null}
            </div>
            <DecisionContextEvidence context={decisionContext} />
          </div>
          <div className="decision-context-actions">
            {decisionContext.amount !== undefined && !contextAmountIsSupport ? (
              <button className="primary-button" type="button" onClick={() => update('revenue', decisionContext.amount ?? 0)}>
                표시 금액 {money(decisionContext.amount)}을 매출 가정에 적용
              </button>
            ) : null}
            {decisionContext.amount !== undefined && contextAmountIsSupport ? (
              <div className="support-amount-context">
                <strong>지원한도 {money(decisionContext.amount)}</strong>
                <span>지원금·보조금은 매출과 의미가 다르므로 매출 가정에 자동 적용하지 않습니다.{decisionContext.selfContributionRate !== undefined ? ` 공식 문구에서 확인된 자부담률은 ${decisionContext.selfContributionRate}%입니다.` : ''}</span>
              </div>
            ) : null}
            {decisionContext.areaSqm !== undefined ? (
              <div className="site-revenue-simulator">
                <label className="studio-field">
                  <span>㎡당 예상 계약매출</span>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={siteRevenuePerSqm || ''}
                    placeholder="직접 입력"
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      setSiteRevenuePerSqm(Number.isFinite(value) && value >= 0 ? value : 0);
                    }}
                  />
                </label>
                <span>면적 기반 예상 계약매출: {money(siteRevenueEstimate)}</span>
                <button className="secondary-button" type="button" disabled={siteRevenueEstimate === null} onClick={() => siteRevenueEstimate !== null && update('revenue', siteRevenueEstimate)}>
                  면적 기반 가정을 매출에 적용
                </button>
              </div>
            ) : null}
            {decisionContext.amount === undefined && decisionContext.areaSqm === undefined ? <span>바로 적용할 공개 금액이 없어 매출·계약금액을 직접 입력해야 합니다.</span> : null}
            {contextAmountIsSupport && decisionContext.areaSqm === undefined ? <span>지원사업의 실제 손익효과는 지원대상 비용·자부담·회계처리에 따라 달라지므로 비용 항목을 직접 조정해 검토하세요.</span> : null}
            <Link className="action-button action-button-secondary action-button-compact" to={decisionContext.sourceRoute}>Radar로 돌아가기</Link>
            <button className="action-button action-button-ghost action-button-compact" type="button" onClick={dismissDecisionContext}>검토 컨텍스트 닫기</button>
          </div>
        </section>
      ) : null}

      {kind === 'profitability' ? (
        <section className="studio-grid" aria-label="수익성 분석">
          <div className="studio-panel">
            <h2>매출·비용 구조</h2>
            <div className="studio-field-grid">
              {(['revenue', 'directCost', 'variableCost', 'fixedCost', 'adSpend'] as NumericKey[]).map((field) => (
                <WorkspaceInput key={field} workspace={workspace} field={field} onChange={update} />
              ))}
            </div>
          </div>
          <div className={`studio-panel studio-results${isStarterWorkspace ? ' studio-results-empty' : ''}`}>
            <h2>현재 구조</h2>
            <Metric label="총비용" value={money(profitability.totalCost)} />
            <Metric label="예상 이익" value={money(profitability.profit)} emphasis />
            <Metric label="순이익률" value={percent(profitability.marginRate)} />
            <Metric label="비용률" value={percent(profitability.costRate)} />
            <p className="studio-insight">{profitability.profit >= 0
              ? `현재 입력에서는 매출 100원당 약 ${Math.max(0, profitability.marginRate).toFixed(1)}원이 남습니다.`
              : '현재 입력에서는 비용이 매출보다 큽니다. 원가·고정비·광고비를 우선 점검하세요.'}</p>
          </div>
        </section>
      ) : null}

      {kind === 'pricing' ? (
        <section className="studio-grid" aria-label="가격 견적 분석">
          <div className="studio-panel">
            <h2>단위 경제성</h2>
            <div className="studio-field-grid">
              {(['unitPrice', 'unitCost', 'unitOtherCost', 'feeRate', 'targetMarginRate'] as NumericKey[]).map((field) => (
                <WorkspaceInput key={field} workspace={workspace} field={field} onChange={update} />
              ))}
            </div>
          </div>
          <div className={`studio-panel studio-results${isStarterWorkspace ? ' studio-results-empty' : ''}`}>
            <h2>가격 판단</h2>
            <Metric label="현재 단위 공헌이익" value={money(pricing.contributionPerUnit)} />
            <Metric label="현재 단위 마진율" value={percent(pricing.currentMarginRate)} />
            <Metric label="목표 마진 판매가" value={money(pricing.targetPrice)} emphasis />
            <Metric label="10% 할인 후 단위이익" value={money(pricing.discount10ProfitPerUnit)} />
            <p className="studio-insight">수수료가 판매가 비율로 차감된다는 가정으로 목표 판매가를 역산합니다.</p>
          </div>
        </section>
      ) : null}

      {kind === 'planning' ? (
        <section className="studio-grid" aria-label="손익 현금흐름 분석">
          <div className="studio-panel">
            <h2>월 운영 가정</h2>
            <div className="studio-field-grid">
              {(['unitPrice', 'unitCost', 'unitOtherCost', 'feeRate', 'fixedCost', 'units', 'targetProfit', 'cashBalance'] as NumericKey[]).map((field) => (
                <WorkspaceInput key={field} workspace={workspace} field={field} onChange={update} />
              ))}
            </div>
          </div>
          <div className={`studio-panel studio-results${isStarterWorkspace ? ' studio-results-empty' : ''}`}>
            <h2>월 손익 계획</h2>
            <Metric label="월 예상매출" value={money(planning.monthlyRevenue)} />
            <Metric label="월 예상이익" value={money(planning.monthlyProfit)} emphasis />
            <Metric label="손익분기 판매량" value={(planning.breakEvenUnits === null ? '계산 불가' : `${planning.breakEvenUnits.toLocaleString('ko-KR')}개`)} />
            <Metric label="손익분기 매출" value={money(planning.breakEvenRevenue)} />
            <Metric label="목표이익 판매량" value={(planning.targetProfitUnits === null ? '계산 불가' : `${planning.targetProfitUnits.toLocaleString('ko-KR')}개`)} />
            <Metric label="적자 지속 시 런웨이" value={(planning.runwayMonths === null ? '현재 적자 아님' : `${planning.runwayMonths.toFixed(1)}개월`)} />
          </div>
        </section>
      ) : null}

      {kind === 'growth' ? (
        <section className="studio-grid" aria-label="광고 성장 분석">
          <div className="studio-panel">
            <h2>광고 가정</h2>
            <div className="studio-field-grid">
              {(['adSpend', 'attributedRevenue', 'grossMarginRate', 'targetProfit'] as NumericKey[]).map((field) => (
                <WorkspaceInput key={field} workspace={workspace} field={field} onChange={update} />
              ))}
            </div>
          </div>
          <div className={`studio-panel studio-results${isStarterWorkspace ? ' studio-results-empty' : ''}`}>
            <h2>광고 의사결정</h2>
            <Metric label="현재 ROAS" value={percent(growth.roas)} />
            <Metric label="손익분기 ROAS" value={percent(growth.breakEvenRoas)} />
            <Metric label="광고 후 예상이익" value={money(growth.estimatedProfitAfterAds)} emphasis />
            <Metric label="현재 매출 기준 최대 광고비" value={money(growth.maxAdSpend)} />
            <Metric label="목표이익 달성 필요매출" value={money(growth.targetRevenue)} />
          </div>
        </section>
      ) : null}

      <section className="studio-footer-links">
        <h2>빠른 계산이 필요하면</h2>
        <p>종합 분석이 아니라 한 가지 값만 계산할 때는 기존 계산기를 그대로 사용할 수 있습니다.</p>
        <Link className="action-button action-button-secondary" to="/calculators">빠른 계산기 열기</Link>
      </section>
    </>
  );
}
